import React, { useState, useEffect } from 'react'
import StartupCard from './StartupCard'
import { useQuery } from '@apollo/client'
import Pin from './svg/Pin'
import PinActive from './svg/PinActive'
import Cross from './svg/Cross'
import PlusToggle from './svg/PlusToggle'
import MinusToggle from './svg/MinusToggle'
import ReactMapGL, { Marker } from 'react-map-gl'
import CustomMultiselect from './CustomMultiselect'
import CustomMultiselectSearch from './CustomMultiselectSearch'
import { STARTUPS, ECOSYSTEMS, INDUSTRIES, LEGAL_FORMS, BUSINESS_MODELS } from '../graphql/queries'
import { getLocationName } from '../utils/getLocationName'
import SponsorLogoNoText from "./svg/SponsorLogoNoText"

function Map() {
    const { loading, data, error } = useQuery(STARTUPS)
    const [unfilteredData, setUnfilteredData] = useState([])
    const [startups, setStartups] = useState([])
    const [filterActive, setFilterActive] = useState(false)

    useEffect(() => {
        if (!error && !loading) {
            setUnfilteredData(data.startups.data)
            setStartups(data.startups.data)
            setStartupSearchOptions(data.startups.data.map(s => ({ value: s.id, label: s.attributes.NAM })))

            // Display locations on map
            const allLocations = data.startups
                .data
                .map(s => s.attributes.LOC.data.attributes)
                .filter((location, index, array) => array.map(l => l.name).indexOf(location.name) === index) // Remove dupes

            setDisplayedPins(allLocations) 
        }
    }, [data, error, loading])

    function handleClickReset() {
        setFilteredEcosystems([])
        setFilteredIndustries([])
        setFilteredLegalForms([])
        setFilteredBusinessModels([])
        setFilteredStartups([])
        setSelectedLocationName(null)
    }

    /* Map pins */
    const [displayedPins, setDisplayedPins] = useState([])

    /* Filter by location name */
    const [selectedLocationName, setSelectedLocationName] = useState()

    useEffect(() => {
        if (selectedLocationName) {
            setFilterActive(true)
            movePinToForeground(selectedLocationName)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocationName])


    /* Ecosystems */
    const [ecosystems, setEcosystems] = useState()
    const [filteredEcosystems, setFilteredEcosystems] = useState([])
    const ecosystemsQuery = useQuery(ECOSYSTEMS)
    useEffect(() => {
        if (ecosystemsQuery.data) {
            const dataReceived = ecosystemsQuery.data
                .ecoRanges
                .data
                .map(ecosystem => ({ value: ecosystem.id, label: ecosystem.attributes.value }))

            setEcosystems(dataReceived)
        }
    }, [ecosystemsQuery.data, ecosystemsQuery.error])
    
    function handleChangeEcosystems(selectedOptions) {
        setFilteredEcosystems(selectedOptions)
        setFilterActive(true)
    }
    function removeEcosystemFromFilter(removedEcosystem) {
        if (filteredEcosystems?.length === 1) {
            setFilteredEcosystems([])
        } else {
            let newFilteredEcosystems = filteredEcosystems.filter(e => e.value !== removedEcosystem.value)
            setFilteredEcosystems(newFilteredEcosystems)
        }
    }

    /* Industry */
    const [industries, setIndustries] = useState()
    const [filteredIndustries, setFilteredIndustries] = useState([])
    const industriesQuery = useQuery(INDUSTRIES)
    useEffect(() => {
        if (industriesQuery.data) {
            const dataReceived = industriesQuery.data
                .indRanges
                .data
                .map(industry => ({ value: industry.id, label: industry.attributes.value }))

            setIndustries(dataReceived)
        }
    }, [industriesQuery.data, industriesQuery.error])

    function handleChangeIndustry(selectedOptions) {
        setFilteredIndustries(selectedOptions)
        setFilterActive(true)
    }
    function removeIndustryFromFilter(removedIndustry) {
        if (filteredIndustries?.length === 1) {
            setFilteredIndustries([])
        } else {
            let newFilteredIndustries = filteredIndustries.filter(i => i.value !== removedIndustry.value)
            setFilteredIndustries(newFilteredIndustries)
        }
    }

    /* Legal form */
    const [legalForms, setLegalForms] = useState()
    const [filteredLegalForms, setFilteredLegalForms] = useState([])
    const legalFormsQuery = useQuery(LEGAL_FORMS)
    useEffect(() => {
        if (legalFormsQuery.data) {
            const dataReceived = legalFormsQuery.data
                .legRanges
                .data
                .map(legalForm => ({ value: legalForm.id, label: legalForm.attributes.value }))

            setLegalForms(dataReceived)
        }
    }, [legalFormsQuery.data, legalFormsQuery.error])

    function handleChangeLegalForm(selectedOptions) {
        setFilteredLegalForms(selectedOptions)
        setFilterActive(true)
    }
    function removeLegalFormFromFilter(removedLegalForm) {
        if (filteredLegalForms?.length === 1) {
            setFilteredLegalForms([])
        } else {
            let newFilteredLegalForms = filteredLegalForms.filter(i => i.value !== removedLegalForm.value)
            setFilteredLegalForms(newFilteredLegalForms)
        }
    }

    /* Business models */
    const [businessModels, setBusinessModels] = useState()
    const [filteredBusinessModels, setFilteredBusinessModels] = useState([])
    const businessModelsQuery = useQuery(BUSINESS_MODELS)
    useEffect(() => {
        if (businessModelsQuery.data) {
            const dataReceived = businessModelsQuery.data
                .modRanges
                .data
                .map(businessModel => ({ value: businessModel.id, label: businessModel.attributes.value }))

            setBusinessModels(dataReceived)
        }
    }, [businessModelsQuery.data, businessModelsQuery.error])

    function handleChangeBusinessModel(selectedOptions) {
        setFilteredBusinessModels(selectedOptions)
        setFilterActive(true)
    }
    function removeBusinessModelFromFilter(removedBusinessModel) {
        if (filteredBusinessModels?.length === 1) {
            setFilteredBusinessModels([])
        } else {
            let newFilteredBusinessModels = filteredBusinessModels.filter(i => i.value !== removedBusinessModel.value)
            setFilteredBusinessModels(newFilteredBusinessModels)
        }
    }

    /* Startup name search */
    const [startupSearchOptions, setStartupSearchOptions] = useState()
    const [filteredStartups, setFilteredStartups] = useState([])
    function handleChangeStartupNameSearch(selectedOptions) {
        setFilteredStartups(selectedOptions)        
        setFilterActive(true)
    }

    function removeStartupFromFilter(val) {
        if (filteredStartups?.length === 1) {
            setFilteredStartups([])
        } else {
            let newFilteredStartups = filteredStartups.filter(v => v.value !== val.value)
            setFilteredStartups(newFilteredStartups)
        }
    }

    /* Filter hook */
    useEffect(() => {
        if (unfilteredData.length > 0) {
            filterData()

            // If no filters are set, adjust the display
            const allFilters = [filteredStartups, filteredEcosystems, filteredIndustries, filteredLegalForms, filteredBusinessModels]
            if (allFilters.every(arr => !arr.length) && !selectedLocationName) {
                setFilterActive(false)
            }
        }        
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filteredStartups,
        filteredEcosystems,
        filteredIndustries,
        filteredLegalForms,
        filteredBusinessModels,
        selectedLocationName
    ])

    /* Filter algorithm */
    function filterData() {
        let filteredData = [...unfilteredData]
        if (filteredStartups.length) {
            const filteredStartupIds = filteredStartups.map(s => s.value)
            filteredData = filteredData.filter(startup =>
                filteredStartupIds.includes(startup.id)
            )
        }

        if (filteredEcosystems.length) {
            const filteredEcosystemIds = filteredEcosystems.map(e => e.value)
            filteredData = filteredData.filter(startup => 
                filteredEcosystemIds.includes(startup.attributes.ECO.data.id)
            )
        }

        if (filteredIndustries.length) {
            const filteredIndustryIds = filteredIndustries.map(i => i.value)
            filteredData = filteredData.filter(startup => 
                filteredIndustryIds.includes(startup.attributes.IND.data.id)
            )
        }

        if (filteredLegalForms.length) {
            const filteredLegalFormIds = filteredLegalForms.map(i => i.value)
            filteredData = filteredData.filter(startup => 
                filteredLegalFormIds.includes(startup.attributes.LEG.data.id)
            )
        }

        if (filteredBusinessModels.length) {
            const filtereBusinessModelIds = filteredBusinessModels.map(i => i.value)
            filteredData = filteredData.filter(startup => 
                filtereBusinessModelIds.includes(startup.attributes.MOD.data.id)
            )
        }

        if (selectedLocationName) {
            filteredData = filteredData.filter(startup => 
                getLocationName(startup) === selectedLocationName         
            )       
        }

        // Filter pins on the map
        const filteredLocations = filteredData
            .map(s => s.attributes.LOC.data.attributes)
            .filter((location, index, array) => array.map(l => l.name).indexOf(location.name) === index) // Remove dupes
        setDisplayedPins(filteredLocations)

        setStartups(filteredData)
    }
    
    //dropdown Menu
    const BREAKPOINT_MEDIUM = 1024
    const [screenWidth, setScreenWidth] = useState(window.innerWidth)
    const [openMenu, setOpenMenu] = useState(false)
    const toggleNavBtn = () => setOpenMenu(!openMenu)

    useEffect(() => {
        const changeWidth = () => {
            setScreenWidth(window.innerWidth)
        }
        window.addEventListener('resize', changeWidth)

        return () => {
            window.removeEventListener('resize', changeWidth)
        }
    }, [])

    const [viewport, setViewport] = useState({
        latitude: 49.8,
        longitude: 7.7,
        width: '100vw',
        height: '100vh',
        zoom: 7.23,
    })

    const movePinToForeground = locationName => {
        // Remove the marker from the array and append it to the end, to display the pin on top
        const currentLocationIndex = displayedPins.map(l => l.name).indexOf(locationName)
    
        if (currentLocationIndex !== -1) {
            const currentLocation = displayedPins[currentLocationIndex]        
            const newPins = [...displayedPins]                                                
            newPins.splice(currentLocationIndex, 1)
            newPins.push(currentLocation)
    
            setDisplayedPins(newPins)    
        }    
    }

    return (
        <div>
            <div className="h-[81px] w-full flex justify-between bg-white items-center px-[25px] lg:hidden">
                <span className="text-xl font-bold">Filter startups</span>
                <span onClick={() => toggleNavBtn()} className="cursor-pointer">
                    {!openMenu && <PlusToggle className="w-10 h-10" />}
                    {openMenu && <MinusToggle />}
                </span>
            </div>

            {(openMenu || screenWidth >= BREAKPOINT_MEDIUM) && (
                <div id="filter-wrapper">
                    <div className="h-[100%] w-full absolute lg:static z-30 overflow-y-scroll">
                        <div className="gap-2 lg:gap-0 w-full z-50 px-[25px] pb-[25px] absolute flex flex-col bg-white lg:pb-0 lg:flex-row lg:items-center lg:space-y-0 lg:space-x-[12px] lg:h-[92px] lg:px-[22px]">
                            <div className="w-full lg:w-2/6 lg:relative">
                                <CustomMultiselectSearch
                                    id="startup-multiselect"
                                    value={filteredStartups}
                                    onChange={handleChangeStartupNameSearch}
                                    options={startupSearchOptions}
                                    placeholder="Find a Startup"
                                />
                            </div>
                            <div className="w-full lg:w-1/6">
                                <CustomMultiselect
                                    id="ecosystem-multiselect"
                                    value={filteredEcosystems}
                                    onChange={handleChangeEcosystems}
                                    options={ecosystems}
                                    placeholder="Startup Ecosystem"
                                />
                            </div>
                            <div className="w-full lg:w-1/6">
                                <CustomMultiselect
                                    id="industry-multiselect"
                                    value={filteredIndustries}
                                    onChange={handleChangeIndustry}
                                    options={industries}
                                    placeholder="Industry"
                                />
                            </div>
                           {/* <div className="w-full lg:w-1/6">
                                <CustomMultiselect
                                    id="legal-form-multiselect"
                                    value={filteredLegalForms}
                                    onChange={handleChangeLegalForm}
                                    options={legalForms}
                                    placeholder="Legal Form"
                                />
                            </div>
                            <div className="w-full lg:w-1/6">
                                <CustomMultiselect
                                    id="business-model-multiselect"
                                    value={filteredBusinessModels}
                                    onChange={handleChangeBusinessModel}
                                    options={businessModels}
                                    placeholder="Business Model"
                                />
                            </div>*/}
                            {filteredStartups?.map(startup => (
                                <div
                                    className="flex items-center px-2 rounded-lg lg:hidden w-max bg-mid-light-grey"
                                    key={startup.label}>
                                    <button
                                        onClick={() => removeStartupFromFilter(startup)}
                                        className="py-0.5 pl-0 pr-1">
                                        <Cross />
                                    </button>
                                    <h3 className="mr-3 text-white font-poppins-regular">
                                        Startup: {startup.label}
                                    </h3>
                                </div>
                            ))}
                            {filteredEcosystems?.map(ecosystem => (
                                <div
                                    className="flex items-center px-2 rounded-lg lg:hidden w-max bg-mid-light-grey"
                                    key={ecosystem.label}>
                                    <button
                                        onClick={() => removeEcosystemFromFilter(ecosystem)}
                                        className="py-0.5 pl-0 pr-1">
                                        <Cross />
                                    </button>
                                    <h3 className="mr-3 text-white font-poppins-regular">
                                        Ecosystem: {ecosystem.label}
                                    </h3>
                                </div>
                            ))}
                            {filteredIndustries?.map(industry => (
                                <div
                                    className="flex items-center px-2 rounded-lg lg:hidden w-max bg-mid-light-grey"
                                    key={industry.label}>
                                    <button
                                        onClick={() => removeIndustryFromFilter(industry)}
                                        className="py-0.5 pl-0 pr-1">
                                        <Cross />
                                    </button>
                                    <h3 className="mr-3 text-white font-poppins-regular">
                                        Industry: {industry.label}
                                    </h3>
                                </div>
                            ))}
                            {filteredLegalForms?.map(legalForm => (
                                <div
                                    className="flex items-center px-2 rounded-lg lg:hidden w-max bg-mid-light-grey"
                                    key={legalForm.label}>
                                    <button
                                        onClick={() => removeLegalFormFromFilter(legalForm)}
                                        className="py-0.5 pl-0 pr-1">
                                        <Cross />
                                    </button>
                                    <h3 className="mr-3 text-white font-poppins-regular">
                                        Legal Form: {legalForm.label}
                                    </h3>
                                </div>
                            ))}
                            {filteredBusinessModels?.map(businessModel => (
                                <div
                                    className="flex items-center px-2 rounded-lg lg:hidden w-max bg-mid-light-grey"
                                    key={businessModel.label}>
                                    <button
                                        onClick={() => removeBusinessModelFromFilter(businessModel)}
                                        className="py-0.5 pl-0 pr-1">
                                        <Cross />
                                    </button>
                                    <h3 className="mr-3 text-white font-poppins-regular">
                                        Business Model: {businessModel.label}
                                    </h3>
                                </div>
                            ))}
                            <div className="lg:hidden w-full flex flex-row justify-between">
                                <button className='w-full mr-1 mt-5 border-black rounded-xl border-3 link-bold' onClick={handleClickReset}>Reset filters</button>
                                <button className='w-full ml-1 mt-5 border-black rounded-xl border-3 link-bold' onClick={toggleNavBtn}>Show results</button>
                            </div>
                            <div className="lg:hidden h-[500px]"></div>
                        </div>
                    </div>
                </div>
            )}
            <div className="flex flex-col w-full lg:w-[420px] h-full ">
                <div className="relative left-0 lg:top-[92px] z-10 w-full lg:w-[380px] flex items-center justify-between h-[60px] lg:h-[75px] bg-light-grey">
                    {startups?.length > 0 && (
                        <div className="flex flex-row">
                            {filterActive && (
                                <h2 className="pl-8">
                                    {startups.length} Startups
                                </h2>
                            )}
                            {!filterActive && (
                                <h2 className="pl-8">All Startups</h2>
                            )}
                        </div>
                    )}
                    {startups?.length === 0 && (
                        <h2 className="mt-4 ml-6 hidden lg:inline-block">No Results</h2>
                    )}
                    <div className="invisible lg:visible">
                        <SponsorLogoNoText/>
                    </div>
                </div>

                <div className="w-full absolute top-[221px] lg:top-[247px] bottom-0 left-0 lg:w-[380px] overflow-y-auto lg:overflow-y-scroll bg-light-grey z-10">
                    {startups?.length === 0 && (
                        <>
                            <div className="flex flex-col justify-items-center">
                                <p className="mx-6 lg:mt-8 copy-main-regular text-center lg:text-left">
                                    We could not find any startups that match your filters.
                                </p>
                                <button
                                    onClick={() => handleClickReset()}
                                    className="py-1 mt-3 mx-6 border-black lg:mt-14 rounded-xl border-3 link-bold">
                                    Reset filters
                                </button>
                            </div>
                        </>
                    )}
                    {startups &&
                        startups.map(startup => <StartupCard key={startup.id} startup={startup} />)
                    }
                    <div className="lg:hidden w-full lg:h-[75px] lg:pb-4">
                        
                        <div className="flex items-center justify-between w-full mt-4 mb-4 lg:w-auto lg:min-w-0 lg:mb-0">
                            <div className="flex items-center">
                                <a
                                    className="ml-4 mr-4 text-[10px]"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href="https://station-frankfurt.de/impressum/">
                                    Legal Notice
                                </a>
                                <a
                                    className="mr-4 text-[10px]"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href="https://station-frankfurt.de/datenschutzerklaerung/">
                                    Privacy Policy
                                </a>
                            </div>
                            <div className="grow">
                                <SponsorLogoNoText/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="lg:block absolute w-auto z-10 top-44 left-[430px] flex-row flex items-center text-white gap-2">
                <div className="flex flex-row invisible gap-2 text-center lg:visible tems-center ">
                    {filteredStartups?.map(startup => (
                        <div
                            className="flex items-center px-2 rounded-lg bg-mid-light-grey"
                            key={startup.label}>
                            <button
                                onClick={() => removeStartupFromFilter(startup)}
                                className="py-0.5 pl-0 pr-1">
                                <Cross />
                            </button>
                            <h3 className="mr-3 text-white font-poppins-regular">
                                Startup: {startup.label}
                            </h3>
                        </div>
                    ))}
                    {filteredEcosystems?.map(ecosystem => (
                        <div
                            className="flex items-center px-2 rounded-lg bg-mid-light-grey"
                            key={ecosystem.label}>
                            <button
                                onClick={() => removeEcosystemFromFilter(ecosystem)}
                                className="py-0.5 pl-0 pr-1">
                                <Cross />
                            </button>
                            <h3 className="mr-3 text-white font-poppins-regular">
                                Ecosystem: {ecosystem.label}
                            </h3>
                        </div>
                    ))}
                    {filteredIndustries?.map(industry => (
                        <div
                            className="flex items-center px-2 rounded-lg bg-mid-light-grey"
                            key={industry.label}>
                            <button
                                onClick={() => removeIndustryFromFilter(industry)}
                                className="py-0.5 pl-0 pr-1">
                                <Cross />
                            </button>
                            <h3 className="mr-3 text-white font-poppins-regular">
                                Industry: {industry.label}
                            </h3>
                        </div>
                    ))}
                    {filteredLegalForms?.map(legalForm => (
                        <div
                            className="flex items-center px-2 rounded-lg bg-mid-light-grey"
                            key={legalForm.label}>
                            <button
                                onClick={() => removeLegalFormFromFilter(legalForm)}
                                className="py-0.5 pl-0 pr-1">
                                <Cross />
                            </button>
                            <h3 className="mr-3 text-white font-poppins-regular">
                                Legal Form: {legalForm.label}
                            </h3>
                        </div>
                    ))}
                    {filteredBusinessModels?.map(businessModel => (
                        <div
                            className="flex items-center px-2 rounded-lg bg-mid-light-grey"
                            key={businessModel.label}>
                            <button
                                onClick={() => removeBusinessModelFromFilter(businessModel)}
                                className="py-0.5 pl-0 pr-1">
                                <Cross />
                            </button>
                            <h3 className="mr-3 text-white font-poppins-regular">
                                Business Model: {businessModel.label}
                            </h3>
                        </div>
                    ))}
                    {selectedLocationName && (
                        <div
                            className="flex items-center px-2 rounded-lg bg-mid-light-grey"
                            key={selectedLocationName}>
                            <button
                                onClick={() => setSelectedLocationName(null)}
                                className="py-0.5 pl-0 pr-1">
                                <Cross />
                            </button>
                            <h3 className="mr-3 text-white font-poppins-regular">
                                Location: {selectedLocationName}
                            </h3>
                        </div>
                    )}

                </div>
            </div>
            <div className="hidden lg:block opacity-30 transition-opacity duration-1000" id="react-map-wrapper">
                <ReactMapGL
                    style={{
                        position: 'absolute',
                        left: '0',
                        top: '172px',
                        bottom: '0',
                        right: '0',
                    }}
                    {...viewport}
                    mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                    mapStyle="mapbox://styles/peteryves/ckueuyyha8lfn17o4vx3vmrsl"
                    onViewportChange={viewport => {
                        setViewport(viewport)
                    }}>
                    {displayedPins && displayedPins                        
                        .map(
                        location =>
                            location.latitude &&
                            location.longitude && (
                                <Marker
                                    className="h-0 w-0"
                                    key={location.name}
                                    latitude={parseFloat(location.latitude)}
                                    longitude={parseFloat(location.longitude)}
                                    offsetTop={
                                        location.name !== selectedLocationName
                                            ? -30
                                            : -42
                                    }
                                    style={{ zIndex: startups
                                        .filter(s => getLocationName(s) === location.name)
                                        .length }}
                                    >
                                    {selectedLocationName !== location.name && (
                                        <Pin
                                            attributes = {{
                                                id: location.name,
                                                number: startups
                                                    .filter(s => getLocationName(s) === location.name)
                                                    .length,
                                                style: { transform: 'translate(-50%, -50%)' },
                                                className: "w-[49px] h-[67px]"
                                            }}
                                            onClickCallback={e => {
                                                e.preventDefault()
                                                setSelectedLocationName(location.name)
                                            }}
                                        />
                                    )}
                                    {selectedLocationName === location.name && (
                                        <PinActive
                                            number={startups
                                                .filter(s => getLocationName(s) === location.name)
                                                .length
                                            }
                                            style={{
                                                transform:
                                                    'translate(-50%, -50%)',
                                            }}
                                            className="w-[73px] h-[92px]"
                                            onClick={e => {
                                                e.preventDefault()
                                                setSelectedLocationName(null)
                                            }}
                                        />
                                    )}
                                </Marker>
                            )
                    )}
                </ReactMapGL>
            </div>
        </div>
    )
}

export default Map
